<template>
  <div class="font-inter bg-[#FFFFFF] rounded-t-[20px] mt-3 pb-10">
    <HeaderComponent page-title="Dashboard" />

    <div class="px-8 pt-4 space-y-6 overflow-hidden">
      <!-- Top header row -->
      <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div class="space-y-1">
          <h1 class="text-[20px] font-semibold text-bodyText">
            Overview
          </h1>
          <p v-if="loading" class="text-[14px] text-grayDark">Loading your workspace…</p>
          <p v-else class="text-[14px] text-grayDark">
            Built for strategic workforce planning. Welcome back {{ user.first_name }}.
          </p>
        </div>

        <div class="relative group self-start md:self-auto">
          <button
            @click="emitOpenCreateProjectModal"
            class="flex bg-primaryDarkBg hover:bg-primaryDark text-primaryLight text-[14px] font-medium px-6 py-3 space-x-4 items-center rounded-[8px] h-[42px]"
            :disabled="goalLimit && goalLimit.is_limit_reached"
          >
            <img src="@/assets/dashboard/add.svg" alt="User icon" class="h-4">
            New Project
          </button>
          <div
            v-if="goalLimit && goalLimit.is_limit_reached"
            class="absolute left-0 top-full mt-2 w-max bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-50"
          >
            You have reached the maximum number of projects you can create.
          </div>
        </div>
      </div>

      <div class="flex justify-center">
        <div
          class="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-[#F9FAFB] border border-grayLight rounded-full px-6 py-4 max-w-3xl w-full"
        >
          <div class="flex items-center gap-3">
            <div class="flex items-baseline gap-2">
              <span class="text-[20px] font-semibold text-bodyText">{{ activeProjectsCount }}</span>
              <span class="text-[12px] uppercase tracking-wide text-grayDark">Active projects</span>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <div class="flex items-baseline gap-2">
              <span class="text-[20px] font-semibold text-bodyText">{{ teamMembersCount }}</span>
              <span class="text-[12px] uppercase tracking-wide text-grayDark">Teams</span>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <div class="flex items-baseline gap-2">
              <span class="text-[20px] font-semibold text-bodyText">{{ averageCompletionRate }}%</span>
              <span class="text-[12px] uppercase tracking-wide text-grayDark">Completion rate</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Main dashboard content -->
      <div class="block space-y-6 md:space-y-0 md:flex md:items-stretch gap-4">
        <div class="w-full md:w-[55%] flex flex-col">
          <ProjectsComponent class="flex-1" />
        </div>
        <div class="w-full md:w-[45%] flex flex-col">
          <TasksSummaryComponent class="flex-1" />
        </div>
      </div>

      <div class="flex flex-col md:flex-row gap-4 overflow-hidden">
        <div class="w-full md:w-[45%] min-w-0">
          <DashboardTeamsCard />
        </div>
        <div class="w-full md:w-[25%] min-w-0">
          <DashboardRecentActivityCard />
        </div>
        <div class="w-full md:w-[30%] min-w-0">
          <DashboardTimeTrackingCard />
        </div>
      </div>

      <!-- Productivity heatmap -->
      <ProductivityHeatmap />
    </div>
  </div>
</template>

<script>
import apiClient from '@/axios'
import HeaderComponent from './HeaderComponent.vue'
import ProjectsComponent from './dashboard/ProjectsComponent.vue'
import TasksSummaryComponent from './dashboard/TasksSummaryComponent.vue'
import ProductivityHeatmap from './dashboard/ProductivityHeatmap.vue'
import DashboardTeamsCard from './dashboard/DashboardTeamsCard.vue'
import DashboardRecentActivityCard from './dashboard/DashboardRecentActivityCard.vue'
import DashboardTimeTrackingCard from './dashboard/DashboardTimeTrackingCard.vue'
import eventBus from '@/eventBus';


export default {
  name: 'DashboardComponent',
  components: {
    HeaderComponent,
    ProjectsComponent,
    TasksSummaryComponent,
    ProductivityHeatmap,
    DashboardTeamsCard,
    DashboardRecentActivityCard,
    DashboardTimeTrackingCard,
  },
  data() {
    return {
      user: {},
      talents: [],
      projects: [],
      users: [],
      loading: true,
      showCreateProjectModal: false,
      isDropdownOpen: false,
      goalLimit: null,
      managerOverview: null,
    }
  },
  computed: {
    isManager() {
      return this.$store.getters.isManager;
    },
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isTalent() {
      return this.$store.getters.isTalent;
    },
    isUser() {
      return this.$store.getters.isUser;
    },

    activeProjectsCount() {
      if (this.managerOverview && typeof this.managerOverview.total_active_projects !== 'undefined') {
        return this.managerOverview.total_active_projects ?? 0;
      }
      return 0;
    },

    teamMembersCount() {
      if (this.managerOverview && typeof this.managerOverview.total_teams !== 'undefined') {
        return this.managerOverview.total_teams ?? 0;
      }
      return 0;
    },

    averageCompletionRate() {
      if (this.managerOverview && typeof this.managerOverview.completion_rate_percentage !== 'undefined') {
        const value = Number(this.managerOverview.completion_rate_percentage ?? 0);
        return isNaN(value) ? 0 : Math.round(value);
      }
      return 0;
    },
  },

  methods: {
    emitOpenCreateProjectModal() {
      eventBus.emit('openCreateProjectModal')
    },
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    async fetchCurrentUser() {
      try {
        const response = await apiClient.post('/current_user')
        this.user = response.data.user
        this.fetchGoalLimit(response.data.user.user_id);
      } catch (error) {
        console.error('Error fetching user data:', error)
      }
    },
    async fetchTalents() {
      try {
        const response = await apiClient.get('/web/general/talents')
        this.talents = response.data.data
      } catch (error) {
        console.error('Error fetching talents:', error)
      }
    },
    async fetchProjects() {
      try {
        const response = await apiClient.get('/web/projects_all', {
          params: {
            active_profile: "manager",
          },
        })
        this.projects = response.data.data
      } catch (error) {
        console.error('Error fetching projects:', error)
      }
    },
    async fetchManagerOverview() {
      try {
        const response = await apiClient.get('/web/dashboard/manager/overview')
        this.managerOverview = response.data.data || response.data
      } catch (error) {
        console.error('Error fetching manager overview:', error)
      }
    },
    async fetchUsers() {
      try {
        if (this.isAdmin) {
        const response = await apiClient.get('web/users')
        this.users = response.data.data
        }
      } catch (error) {
        console.error('Error fetching users:', error)
      } finally {
        this.loading = false
      }
    },
    async fetchGoalLimit(userId) {
      try {
        const response = await apiClient.get('/web/limit/', {
          params: {
            id: userId,
            name: "project",
            limit_type: "user",
          },
        });
        this.goalLimit = response.data.data;
      } catch (error) {
        console.error('Error fetching goal limit:', error);
      }
    },
    goToProject(projectId) {
      this.$router.push({ name: 'ProjectDetails', params: { projectId } })
    },
  },
  created() {
    this.fetchCurrentUser();
    this.fetchGoalLimit();
    this.fetchProjects();
    this.fetchUsers();
    this.fetchManagerOverview();
  }
}
</script>

<style scoped>
.overlap {
  margin-left: -10px;
  border-radius: 50%;
}
.smoke-light {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>